import { FC, useCallback } from 'react';
import classnames from 'classnames';

import { DisabledContext } from '@hh.ru/magritte-common-use-disabled';
import { Action } from '@hh.ru/magritte-ui-action';
import { BannerProps } from '@hh.ru/magritte-ui-banner/types';
import { Card } from '@hh.ru/magritte-ui-card';
import type { CardPadding, CardBorderRadius } from '@hh.ru/magritte-ui-card/types';
import { CrossOutlinedSize24 } from '@hh.ru/magritte-ui-icon/icon';

import styles from './banner.less';

export const Banner: FC<BannerProps> = ({
    stretched = false,
    showBorder = false,
    style,
    disabled = false,
    styleClose = 'neutral',
    content,
    showClose = true,
    visible = true,
    onClose,
    title,
    buttonBase,
    buttonAdditional,
    'aria-label-close': ariaLabelClose,
}) => {
    const handleClose = useCallback(() => {
        onClose?.();
    }, [onClose]);

    const cardProps = {
        stretched,
        showBorder,
        padding: 16 as CardPadding,
        paddingRight: 16 as CardPadding,
        borderRadius: 16 as CardBorderRadius,
        borderBottomLeftRadius: buttonBase || buttonAdditional ? (24 as CardBorderRadius) : (16 as CardBorderRadius),
        borderBottomRightRadius: buttonBase || buttonAdditional ? (24 as CardBorderRadius) : (16 as CardBorderRadius),
    };

    if (style !== undefined) {
        Object.assign(cardProps, { style });
    }

    return (
        <>
            {visible && (
                <DisabledContext.Provider value={disabled}>
                    <Card {...cardProps}>
                        <div
                            className={classnames(styles.bannerContent, {
                                [styles.bannerContentPadding]: showClose,
                            })}
                        >
                            {title && (
                                <>
                                    {title}
                                    <div className={styles.bannerTitlePadding}></div>
                                </>
                            )}
                            {showClose && (
                                <div className={styles.bannerClose}>
                                    <Action
                                        aria-label={ariaLabelClose}
                                        icon={CrossOutlinedSize24}
                                        mode="secondary"
                                        onClick={handleClose}
                                        style={styleClose}
                                        data-qa="banner-close"
                                    />
                                </div>
                            )}
                            {content}
                            {(buttonBase || buttonAdditional) && (
                                <div className={styles.bannerButtonContainer}>
                                    {buttonBase}
                                    {buttonAdditional}
                                </div>
                            )}
                        </div>
                    </Card>
                </DisabledContext.Provider>
            )}
        </>
    );
};
